import React, { useContext, FunctionComponent, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// MUI Compoenents
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

// Custom Components
import useFormController, { SubmodelAPIPaths } from '@/components/useFormController';
import FormContent from '@/components/FormContent';
import DynamicDialog from '@/ui/Dialog/DynamicDialog';
import DialogToolbarHeading from '@/ui/Dialog/DialogToolbarHeading';
import DialogToolbarButtonClose from '@/ui/Dialog/ToolbarButtons/DialogToolbarButtonClose';
import ToolbarFillContent from '@/ui/Toolbar/ToolbarFillContent';
import DialogActionButtonAdd from '@/ui/Dialog/ActionButtons/DialogActionButtonAdd';
import { GridContainer} from '@/ui/Grid';
import DialogActionButtonCancel from '@/ui/Dialog/ActionButtons/DialogActionButtonCancel';

// Contexts
import DialogContext, { IDialogProps } from '@/context/DialogContext/DialogContext';
import SnackbarContext from '@/context/SnackbarContext/SnackbarContext';

// Types
import { ClosingDetails } from '@/@types/components/formController';
import DialogActionButtonSave from '@/ui/Dialog/ActionButtons/DialogActionButtonSave';

const SearchSettingsDialog: FunctionComponent<IDialogProps> = (props) => {
  const { hideDialog } = useContext(DialogContext);
  const { showNotification } = useContext(SnackbarContext);
  const { t } = useTranslation();

  const { dc, mode, form, dataId, onClose, initialRecord, tenantId } = props;

  const [submodelAPIPath, setSubmodelApiPath] = useState<SubmodelAPIPaths | undefined>(tenantId ? {
    toponim_tipovi_picker: `system/tenants/${String(tenantId)}/toponim-tipovi?view=picker`
  } : undefined);

  const {
    record,
    validation,
    fields,
    pickerDataControllers,
    onFieldChange,
    doInsert,
    doUpdate,
    doClose,
  } = useFormController({
    mode,
    recordId: dataId,
    form,
    dc,
    onClose,
    alternativeSubmodelAPIPath: submodelAPIPath,
    initialRecord
  });

  useEffect(() => {
    if(tenantId !== undefined) {
      setSubmodelApiPath({
        toponim_tipovi_picker: `system/tenants/${String(tenantId)}/toponim-tipovi?view=picker`
      })
    }
  }, [tenantId]);

  const handleFieldChange = (value: any, source: string) => {
    if (onFieldChange) {
      onFieldChange(value, source);
    }
  };

  const handleInsert = () => {
    if (doInsert) {
      doInsert().then((result) => {
        if (result.success) {
          const msg = 'messages.insert_toponim_tip_success';
          showNotification(msg, 'success');
          close({ dataChanged: true, action: 'insert', id: result.id });
        } else if (result.error) {
          const msg = 'error.insert_toponim_tip_success';
          showNotification(msg, 'error');
        }
      })
      .catch((resp) => {
        showNotification(resp.error, 'error');
      });
    }
  };

  const handleClose = () => {
    if (doClose) {
      doClose().then((result) => {
        if (result.success) {
          close({ dataChanged: false, action: 'cancel' });
        }
      })
      .catch(() => {});
    }
  };

  const handleUpdate = () => {
    if (doUpdate) {
      doUpdate().then((result) => {
        if (result.success) {
          const msg = 'messages.update_toponim_tip_success';
          showNotification(msg, 'success');
          close({ dataChanged: true, action: 'update' });
        } else if (result.error) {
          const msg = 'error.update_toponim_tip_success';
          showNotification(msg, 'error');
        } 
      })
      .catch((resp) => {
        showNotification(resp.error, 'error');
      });
    }
  };


  const close = (result: ClosingDetails) => {
    onClose(result);
    hideDialog();
  };

  return (
    <DynamicDialog open maxWidth="sm" onClose={handleClose}>
      <DialogTitle>
        <Toolbar variant="dense" disableGutters>
          <DialogToolbarHeading>
            {t('core:titles.toponim_tipovi')}
          </DialogToolbarHeading>
          <ToolbarFillContent />
          <DialogToolbarButtonClose onClick={handleClose} />
        </Toolbar>
      </DialogTitle>
      <DialogContent>
          <GridContainer direction="row" spacing={2} my={2}>
            <FormContent
              title={t('admin:titles.search_settings_information') as string}
              fieldNames={dc.getFormFieldsNames(form)}
              record={record}
              validation={validation}
              fields={fields}
              onFieldChange={handleFieldChange}
              mode={mode}
              columns={1}
              pickerDataControllers={pickerDataControllers}
            />
            <Box height={200} />
          </GridContainer>
      </DialogContent>
      <DialogActions>
        <DialogActionButtonCancel variant="outlined" onClick={handleClose} />
        {mode === 'insert' ? (
          <DialogActionButtonAdd variant="contained" onClick={handleInsert} />
        ) : mode === 'update' ? (
          <DialogActionButtonSave variant="contained" onClick={handleUpdate} />
        ) : null}
      </DialogActions>
    </DynamicDialog>
  );
};

export default SearchSettingsDialog;
